/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { EnumDictionary, TextDictionary } from '@gh/common-shared';
import { IMAGE_TYPES } from '../../../resources/hlp.model';
import { AutocompleteLoadMoreEvent, FormMode, TinymceSettings, tmSettingsBuilder, validationError } from '@gh/core-ui';
import { HlpDetailsBusinessService } from '../../../resources/hlp-details-business.service';
import {
  BaseDesignDetails,
  BaseDesignImage,
  BaseDesignListItemDto,
  BaseDesignRestService,
  BuilderPermission,
  HlpDesignImage,
  HlpStatus,
  Storeys,
} from '@gh/builder-shared-data';
import { TranslationService } from '@gh/core-mls';
import { BuilderUserContext } from '@gh/builder-shared';
import { Validators } from '@angular/forms';
import { sortByStringField } from '@gh/core-util';
import { AbstractControl } from '@angular/forms';

@Component({
  selector: 'gh-hlp-details-design',
  template: require('./hlp-details-design-tab.component.html'),
  styles: [require('./hlp-details-design-tab.component.scss')],
})
export class HlpDetailsDesignTabComponent implements OnInit {
  @ViewChild('galleryImage') galleryImageEl: ElementRef;

  @Input() landAreaUomList: TextDictionary;
  @Input() storeys: EnumDictionary<Storeys>;

  activeTab: 'FACADE' | 'FLOOR_PLANS' | 'IMAGES';
  isShowImageModal: boolean;
  originImage: string;
  imageWidth: number;
  isZoomInDisable: boolean;
  isZoomOutDisable: boolean;
  disableNext: boolean;
  disablePrev: boolean;
  defaultImageWidth = 60;
  zoomStep = 10;
  maxZoom = 100;
  minZoom = 30;
  editor: any;
  tmSettings: TinymceSettings;
  maxAdditionalFeaturesLength = 6;
  maxDescriptionLength = 2500;
  emptyTinyStringLength = 8;
  bedrooms: number[];
  bathrooms: number[];
  carSpaces: number[];

  private readonly EDITOR_CONTROLS = {
    description: 'designDetails.websiteDescription',
    descriptionText: 'designDetails.websiteDescriptionText'
  };

  get isSelectAllImageChecked(): boolean {
    const images: HlpDesignImage[] = this.businessService.mainForm.get('designDetails.images')?.value || [];
    return !!images.length && !images.find((image) => !image.isSelected);
  }

  get floorPlaceholder(): string {
    const floorAreaUom = this.businessService.mainForm.get('designDetails.floorAreaUom')?.value;
    return `${this.translate.get('lbl_floor_area')} ${floorAreaUom ? ', ' + floorAreaUom : ''}`;
  }

  get isShowAddFeatures(): boolean {
    const control = this.businessService.mainForm.get('designDetails.additionalFeatures');
    return control.value.length < this.maxAdditionalFeaturesLength
      && this.businessService.hlpDetails.status !== HlpStatus.Deleted
      && (
        this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT) ||
        this.userContext.hasPermission(BuilderPermission.Sales.Hlp.CREATE)
      );
  }

  get isEditableFeatures(): boolean {
    return this.businessService.hlpDetails.status !== HlpStatus.Deleted
      && (
        this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT) ||
        this.userContext.hasPermission(BuilderPermission.Sales.Hlp.CREATE)
      );
  }

  get descriptionLength(): number {
    if (this.editor) {
      return this.editor.getContent({ format: 'text' }).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').replace(/\n+/g, ' ').replace(/&amp;/g, '&') .trim().length;
    }
    return 0;
  }

  constructor(
    public businessService: HlpDetailsBusinessService,
    private baseDesignService: BaseDesignRestService,
    public translate: TranslationService,
    private userContext: BuilderUserContext,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.setDefaultListValues();

  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    if (this.isShowImageModal) {
      this.closeImageModal();
    }
  }

  ngOnInit(): void {
    this.activeTab = 'FACADE';
    this.imageWidth = this.defaultImageWidth;
    const builder = tmSettingsBuilder()
      .noFontFamilySelector()
      .pasteOptions({ asText: false })
      .scayt()
      // tslint:disable-next-line:no-magic-numbers
      .height(270)
      .statusBar(false)
      .build();
    this.tmSettings = {
      ...builder,
      // @ts-ignore
      toolbar: [...builder.toolbar],
      // @ts-ignore
      plugins: [...builder.plugins, 'autoresize'],
      // @ts-ignore
      paste_auto_cleanup_on_paste: true,
      min_height: 150,
      max_height: 410,
      paste_filter_drop: false,
      autoresize_bottom_margin: 0,
      autoresize_overflow_padding: 10,
      setup: (editor: any) => {
        editor.on('KeyUp', () => {
          this.onDescriptionChange({ editor });
        });
        editor.on('KeyDown', () => {
          this.onDescriptionChange({ editor });
        });
        editor.on('input', () => {
          this.onDescriptionChange({ editor });
        });
        editor.on('Undo', () => {
          this.onDescriptionChange({ editor });
        });
        editor.on('Redo', () => {
          this.onDescriptionChange({ editor });
        });
      },
      onChange: (event: any) => {
        this.onDescriptionChange(event);
      },
      onPaste: (event: any) => {
        this.onDescriptionChange(event);
      },
      onInit: (editor: any) => {
        this.editor = editor;
        editor.on('KeyUp', () => {
          this.onDescriptionChange({ editor });
        });
        editor.on('Undo', () => {
          this.onDescriptionChange({ editor });
        });
        editor.on('Redo', () => {
          this.onDescriptionChange({ editor });
        });
      },
      onLoadContent: (editor: any) => {
        setTimeout(() => {
          const text = editor.getContent({ format: 'text' });
          if (!this.businessService || !this.businessService.mainForm) {
            return;
          }
          const normalizedText = this.normalizeText(text);         
          const control = this.businessService.mainForm.get('designDetails.websiteDescriptionText');
          control.setValue(normalizedText);
          control.setValidators([Validators.required, Validators.maxLength(this.maxDescriptionLength)]);
          control.markAsPristine();
          if (normalizedText.length > this.maxDescriptionLength) {
            const websiteDescriptionControl = this.businessService.mainForm.get('designDetails.websiteDescription');
            websiteDescriptionControl.setErrors({
              maxlength: validationError('msg_field_maxlength', {
                requiredLength: this.maxDescriptionLength,
              }),
            });
            websiteDescriptionControl.markAsTouched();
            websiteDescriptionControl.markAsDirty();
            this.businessService.mainForm.highlightAll();
          }
          // tslint:disable-next-line:no-magic-numbers
        }, 300);

      },
    };
  }

  setDefaultListValues(): void {
    this.bedrooms = [];
    this.carSpaces = [];
    this.bathrooms = [];
    // tslint:disable-next-line:no-magic-numbers
    for (let i = 1; i <= 10; i++) {
      this.bedrooms.push(i);
      this.carSpaces.push(i);
      this.bathrooms.push(i);
      // tslint:disable-next-line:no-magic-numbers
      if (i < 10) {
        // tslint:disable-next-line:no-magic-numbers
        this.bathrooms.push(i + 0.5);
      }
    }
  }

  sortImages(baseDesign?: BaseDesignDetails): void {
    const facadesControl = this.businessService.mainForm.get('designDetails.facades');
    const floorPlansControl = this.businessService.mainForm.get('designDetails.floorPlans');
    const imagesControl = this.businessService.mainForm.get('designDetails.images');
    if (!baseDesign) {
      facadesControl.setValue([]);
      floorPlansControl.setValue([]);
      imagesControl.setValue([]);
      return;
    }
    const facades = baseDesign.facades || [];
    facadesControl.setValue(sortByStringField(facades.map((item) => this.mapImage(item, IMAGE_TYPES.facade)), 'name'));
    imagesControl.setValue(baseDesign.images.map((item) => this.mapImage(item, IMAGE_TYPES.image)));
    floorPlansControl.setValue(baseDesign.floorPlans.map((item) => this.mapImage(item, IMAGE_TYPES.floorPlan)));
  }

  loadDesignsPersons(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findDesignsByName(event.query, {
      showInactive: false,
    }));
  }

  mapImage(item: BaseDesignImage, type: number): HlpDesignImage {
    return {
      name: item.name || item.facadeTypeName || '',
      type,
      isSelected: type === IMAGE_TYPES.floorPlan,
      externalId: item.id,
      websiteLinks: {
        big: item.links.big,
        small: item.links.small,
        medium: item.links.medium,
        original: item.links.original,
      },
    };
  }

  onDesignSelected(design: BaseDesignListItemDto): void {
    if (design && design.name && design.id && design.id !== this.businessService.hlpDetails.designDetails.baseDesign?.id) {
      this.baseDesignService.get(design.id).subscribe((designDto) => {
        if (!designDto) {
          return;
        }
        this.businessService.hlpDetails.designDetails.baseDesign = {
          ...this.businessService.hlpDetails.designDetails.baseDesign,
          id: design.id,
          name: design.name
        }
        this.businessService.mainForm.get('designDetails.bathrooms').setValue(designDto.bathrooms);
        this.businessService.mainForm.get('designDetails.bedrooms').setValue(designDto.bedrooms);
        this.businessService.mainForm.get('designDetails.designSeriesName').setValue(designDto.designSeriesName);
        this.businessService.mainForm.get('designDetails.storeys').setValue(designDto.storeys);
        this.businessService.mainForm.get('designDetails.floorArea').setValue(designDto.floorArea.value);
        this.businessService.mainForm.get('designDetails.carSpaces').setValue(designDto.carSpaces);
        this.businessService.mainForm.get('designDetails.websiteDescription').setValue(designDto.description);
        this.businessService.mainForm.get('designDetails.features').setValue(designDto.features);
        this.businessService.mainForm.get('designDetails.additionalFeatures').setValue(designDto.additionalFeatures);
        this.businessService.mainForm.get('designDetails.floorAreaUom').setValue(designDto.floorArea.unit);
        this.sortImages(designDto);
      });
      return;
    }
    if (!design) {
      this.businessService.mainForm.get('designDetails.bathrooms').setValue(undefined);
      this.businessService.mainForm.get('designDetails.bedrooms').setValue(undefined);
      this.businessService.mainForm.get('designDetails.designSeriesName').setValue(undefined);
      this.businessService.mainForm.get('designDetails.storeys').setValue(undefined);
      this.businessService.mainForm.get('designDetails.floorArea').setValue(undefined);
      this.businessService.mainForm.get('designDetails.features').setValue([]);
      this.businessService.mainForm.get('designDetails.additionalFeatures').setValue([]);
      this.businessService.mainForm.get('designDetails.floorAreaUom').setValue('');
      this.sortImages();
    }
  }

  onSelectTab(event: any): void {
    this.activeTab = event;
    this.galleryImageEl.nativeElement.scrollTo({ top: 0 });
  }

  onImageClick(item: HlpDesignImage, i: number): void {
    const gallery = this.getCurrentGallery();
    this.originImage = item.websiteLinks.original;
    this.isShowImageModal = true;
    this.disablePrev = !i;
    this.disableNext = i === gallery.length - 1;
  }

  onSelectAllImage(event: any): void {
    event.stopPropagation();
    event.preventDefault();
    if ((
      this.businessService.getFormMode(this.businessService.hlpDetails) === FormMode.EditMode
      && !this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT)
    ) ||
      (
        this.businessService.getFormMode(this.businessService.hlpDetails) === FormMode.CreateMode
        && !this.userContext.hasPermission(BuilderPermission.Sales.Hlp.CREATE)
      )) {
      return;
    }
    const isSelectAllImageChecked = this.isSelectAllImageChecked;
    const control = this.businessService.mainForm.get('designDetails.images');
    const images: HlpDesignImage[] = control.value || [];
    images.forEach((image) => image.isSelected = !isSelectAllImageChecked);
    control.setValue(images);
  }
  isImageDisabled(image: HlpDesignImage): boolean {
    return image.type === IMAGE_TYPES.floorPlan;
  }

  isFacadeDisabled(): boolean {
    return this.businessService.hlpDetails.status === HlpStatus.Active ||
      this.businessService.hlpDetails.status === HlpStatus.Deleted;
  }

  closeImageModal(): void {
    this.isShowImageModal = false;
    this.imageWidth = this.defaultImageWidth;
    this.recalculateZoomStatus();
  }

  onImageContainerClick(event: any): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onZoomIn(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.imageWidth < this.maxZoom) {
      this.imageWidth = this.imageWidth + this.zoomStep;
    }
    this.recalculateZoomStatus();
  }

  onZoomOut(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.imageWidth > this.minZoom) {
      this.imageWidth = this.imageWidth - this.zoomStep;
    }
    this.recalculateZoomStatus();
  }

  onNext(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    let index;
    const gallery = this.getCurrentGallery();
    gallery.forEach((item, i) => {
      if (item.websiteLinks.original === this.originImage) {
        index = i + 1;
      }
    });
    if (index && gallery[index]) {
      this.originImage = gallery[index].websiteLinks.original;
    }
    this.imageWidth = this.defaultImageWidth;
    if (index === gallery.length - 1) {
      this.disableNext = true;

    }
    if (gallery.length > 1) {
      this.disablePrev = false;
    }
  }

  onPrev(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    let index;
    const gallery = this.getCurrentGallery();
    gallery.forEach((item, i) => {
      if (item.websiteLinks.original === this.originImage) {
        index = i - 1;
      }
    });
    // @ts-ignore
    if (index >= 0 && gallery[index]) {
      // @ts-ignore
      this.originImage = gallery[index].websiteLinks.original;
    }
    this.imageWidth = this.defaultImageWidth;
    if (!index) {
      this.disablePrev = true;
    }
    if (gallery.length > 1) {
      this.disableNext = false;
    }
  }
  onFacadeSelect(event: any, image: HlpDesignImage): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.hasEditPermissionForImage()) {
      return;
    }
    if (this.isFacadeDisabled() || !image.name) {
      return;
    }
    const control = this.businessService.mainForm.get('designDetails.facades');
    const selected = !image.isSelected;
    control.setValue(control.value.map((item: HlpDesignImage) => {
      const isTargetImage = (item.externalId && image.externalId && item.externalId === image.externalId) ||
        (item.hlpImageId && image.hlpImageId && item.hlpImageId === image.hlpImageId);
      return {
        ...item,
        isSelected: !!selected && !!isTargetImage,
      };
    }));
    control.markAsDirty();
    control.markAsTouched();
  }
  onImageSelect(event: any, image: HlpDesignImage): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.hasEditPermissionForImage()) {
      return;
    }
    if (this.isImageDisabled(image) || !image.name || image.type === IMAGE_TYPES.floorPlan) {
      return;
    }
    const control = this.businessService.mainForm.get('designDetails.images');
    const selected = !image.isSelected;
    const images: HlpDesignImage[] = control.value || [];
    images.forEach((item) => {
      if (
        (item.externalId && image.externalId && item.externalId === image.externalId) ||
        (item.hlpImageId && image.hlpImageId && item.hlpImageId === image.hlpImageId)
      ) {
        item.isSelected = selected;
      }
    });
    control.setValue(images);
    control.markAsDirty();
    control.markAsTouched();
  }
  onDeleteAdditionalFeatures(i: number): void {
    const control = this.businessService.mainForm.get('designDetails.additionalFeatures');
    const additionalFeatures = control.value;
    additionalFeatures.splice(i, 1);
    control.setValue(additionalFeatures);
    control.markAsDirty();
  }

  onAddAdditionalFeatures(): void {
    const control = this.businessService.mainForm.get('designDetails.additionalFeatures');
    if (control.value?.length < this.maxAdditionalFeaturesLength) {
      const additionalFeatures = control.value;
      additionalFeatures.push('');
      control.setValue(additionalFeatures);
      control.markAsDirty();
    }
  }

  onChangeAdditionalFeature(event: any, i: number): void {
    const control = this.businessService.mainForm.get('designDetails.additionalFeatures');
    const additionalFeatures = control.value;
    additionalFeatures[i] = event.target.value;
    control.setValue(additionalFeatures);
    control.markAsDirty();
  }

  identify(item: string): any {
    return item;
  }

  imageIdentify(index: number, item: HlpDesignImage): any {
    return item.externalId || item.hlpImageId;
  }

  onDescriptionChange(event: any): void {
    const editor = event.editor;
    const editorState = this.getEditorState(editor);
    const control = this.getDescriptionControl();
    const bookmark = editor.selection.getBookmark(2, true);
    this.updateDescriptionText(editorState.normalizedText);    
    if (this.handleEmptyOrUnchangedContent(editorState, control, editor, bookmark)) {
        return;
    }
    
    this.updateAndValidateContent(editorState, control);
    
    editor.selection.moveToBookmark(bookmark);
    this.changeDetectorRef.detectChanges();
  }

  private getEditorState(editor: any) {
    const text = editor.getContent({ format: 'text' });
    const textHTML = editor.getContent();
    const originalText = this.businessService.hlpDetails.designDetails.websiteDescription;
    const normalizedText = this.normalizeText(text);
    
    return { text, textHTML, originalText, normalizedText };
  }

  private getDescriptionControl() {
    return this.businessService.mainForm.get(this.EDITOR_CONTROLS.description);
  }

  private updateDescriptionText(normalizedText: string): void {
    this.businessService.mainForm
        .get(this.EDITOR_CONTROLS.descriptionText)
        .setValue(normalizedText);
  }

  private handleEmptyOrUnchangedContent(
      state: { text: string; textHTML: string; originalText: string },
      control: AbstractControl,
      editor: any,
      bookmark: any
  ): boolean {
      if (!state.text || !state.text.trim().length) {
          control.setValue('');
          control.markAsPristine();
          control.setErrors(null);
          return true;
      }

      if (state.textHTML === state.originalText || state.text === state.originalText) {
          control.setValue(state.originalText);
          control.markAsPristine();
          control.setErrors(null);
          editor.selection.moveToBookmark(bookmark);
          return true;
      }

      return false;
  }

  private updateAndValidateContent(
      state: { textHTML: string; normalizedText: string },
      control: AbstractControl
  ): void {
      control.setValue(state.textHTML);

      if (state.normalizedText.length > this.maxDescriptionLength) {
          control.setErrors({
              maxlength: validationError('msg_field_maxlength', {
                  requiredLength: this.maxDescriptionLength,
                  actualLength: state.normalizedText.length
              }),
          });
      } else {
          control.setErrors(null);
      }

      control.markAsDirty();
      control.markAsTouched();
  }

  private normalizeText(text: string): string {
    return text
        .replace(/<[^>]*>/g, ' ')  
        .replace(/\s+/g, ' ')      
        .replace(/\n+/g, ' ')      
        .replace(/&amp;/g, '&')   
        .trim();
  }

  private getCurrentGallery(): HlpDesignImage[] {
    let gallery: HlpDesignImage[];
    switch (this.activeTab) {
      case 'FACADE':
        gallery = this.businessService.mainForm.get('designDetails.facades')?.value;
        break;
      case 'FLOOR_PLANS':
        gallery = this.businessService.mainForm.get('designDetails.floorPlans')?.value;
        break;
      default:
        gallery = this.businessService.mainForm.get('designDetails.images')?.value;
        break;
    }
    return gallery;
  }

  private hasEditPermissionForImage(): boolean {
    if ((
      this.businessService.getFormMode(this.businessService.hlpDetails) === FormMode.EditMode
      && !this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT)
    ) ||
      (
        this.businessService.getFormMode(this.businessService.hlpDetails) === FormMode.CreateMode
        && !this.userContext.hasPermission(BuilderPermission.Sales.Hlp.CREATE)
      )) {
      return false;
    }
    return true;
  }

  private recalculateZoomStatus(): void {
    this.isZoomInDisable = this.imageWidth === this.maxZoom;
    this.isZoomOutDisable = this.imageWidth === this.minZoom;
  }
}
